// Lógica pura de la "Memoria aprendida" (sin React ni i18n): normaliza la
// respuesta del BFF, valida el borrador que se puede mandar y resuelve el
// reemplazo de un dato.
//
// El backend todavía se está construyendo: TODA lectura de nombres de campos de
// respuesta vive acá. Alinear el contrato es tocar estos normalizadores y nada
// más; la vista solo consume las estructuras ya resueltas.
//
// Los mensajes de validación son claves en español: la vista los traduce con
// t(...) al pintarlos.

export const MEMORY_TOPIC_MAX = 80;
export const MEMORY_CONTENT_MAX = 600;
export const MEMORY_BACKFILL_DAYS = 30;
// Tope del bloque de memoria en el prompt (AI_LEARNED_MEMORY_PROMPT_MAX). El
// GET lo manda como `max_chars`; esta copia cubre un backend que no lo mande.
export const MEMORY_PROMPT_MAX_CHARS = 20000;

export const MEMORY_AUDIENCES = ["customer", "merchant", "driver", "all"];
export const MEMORY_VIEWS = ["pending", "active", "disabled"];

const text = (value) => (typeof value === "string" ? value : "");

const textOrNull = (value) => (typeof value === "string" ? value : null);

/** Audiencias válidas, sin duplicados, en cualquier orden de entrada. */
function normalizeAudiences(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  return raw.filter((id) => {
    if (!MEMORY_AUDIENCES.includes(id) || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

/** Contador del backend: entero ≥ 0; cualquier otra cosa cae a 0. */
const toCount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};

function normalizeSupersedes(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const id = raw.id || raw._id;
  return {
    id: id ? String(id) : null,
    topic: text(raw.topic),
    content: text(raw.content),
  };
}

/**
 * Normaliza un Fact del contrato. Devuelve null sin id: una fila sin
 * identificador no se puede editar ni borrar, así que no se pinta.
 * @param {unknown} raw
 * @returns {object|null}
 */
export function normalizeFact(raw) {
  if (!raw || typeof raw !== "object") return null;
  const id = raw.id || raw._id;
  if (!id) return null;

  const src = raw.source && typeof raw.source === "object" ? raw.source : {};

  return {
    id: String(id),
    topic: text(raw.topic),
    content: text(raw.content),
    audiences: normalizeAudiences(raw.audiences),
    status: text(raw.status),
    source: {
      kind: textOrNull(src.kind),
      consult_id: textOrNull(src.consult_id),
      consult_code: textOrNull(src.consult_code),
      area: textOrNull(src.area),
      answered_by: textOrNull(src.answered_by),
      // Se pasa tal cual (ISO, Date o epoch): RelativeTime acepta los tres.
      answered_at: src.answered_at ?? null,
      question: textOrNull(src.question),
      answer: textOrNull(src.answer),
    },
    supersedes_id:
      typeof raw.supersedes_id === "string" && raw.supersedes_id ? raw.supersedes_id : null,
    supersedes: normalizeSupersedes(raw.supersedes),
    edited: raw.edited === true,
    created_at: raw.created_at ?? null,
    updated_at: raw.updated_at ?? null,
  };
}

/**
 * Respuesta de la lista. Acepta `facts` o el alias `items` (backend en
 * transición) y completa los contadores y el tope que falten.
 * @param {unknown} res
 * @returns {{facts: object[], counts: {pending: number, active: number, disabled: number}, activeChars: number|null, maxChars: number, truncated: boolean}}
 */
export function normalizeMemoryResponse(res) {
  const payload = res && typeof res === "object" ? res : {};
  const list = Array.isArray(payload.facts)
    ? payload.facts
    : Array.isArray(payload.items)
      ? payload.items
      : [];
  const counts = payload.counts && typeof payload.counts === "object" ? payload.counts : {};

  return {
    facts: list.map(normalizeFact).filter(Boolean),
    counts: {
      pending: toCount(counts.pending),
      active: toCount(counts.active),
      disabled: toCount(counts.disabled),
    },
    activeChars:
      typeof payload.active_chars === "number" && Number.isFinite(payload.active_chars)
        ? payload.active_chars
        : null,
    maxChars: toCount(payload.max_chars) || MEMORY_PROMPT_MAX_CHARS,
    truncated: payload.truncated === true,
  };
}

/**
 * Respuesta de una mutación (`{ ok, fact }`) o el fact suelto.
 * @param {unknown} res
 * @returns {object|null}
 */
export function normalizeFactResponse(res) {
  if (!res || typeof res !== "object") return null;
  return normalizeFact(res.fact ?? res);
}

/**
 * Resultado del aprendizaje de consultas pasadas. El backend responde 202 y
 * extrae en segundo plano: `started` dice si arrancó (false = ya había uno en
 * curso para este courier) y `candidates` cuántas consultas va a revisar. Lo
 * que salga llega después como pendiente, por eso acá no hay "creados".
 * @param {unknown} res
 * @returns {{started: boolean, candidates: number, skippedNoAgent: number}}
 */
export function normalizeBackfillResponse(res) {
  const payload = res && typeof res === "object" ? res : {};
  return {
    started: payload.started === true,
    candidates: toCount(payload.candidates),
    // Consultas del período cuyo agente ya no existe o no tiene proveedor: se
    // cuentan en `candidates` pero no se revisan.
    skippedNoAgent: toCount(payload.skipped_no_agent),
  };
}

/**
 * Errores del borrador, por campo. `{}` significa que se puede enviar.
 * Los límites se miden sobre el texto ya recortado: los espacios que el backend
 * tampoco guarda no deberían hacer fallar el formulario.
 * @param {{topic?: string, content?: string, audiences?: string[]}} draft
 * @returns {{topic?: string, content?: string, audiences?: string}}
 */
export function validateFactDraft({ topic, content, audiences } = {}) {
  const errors = {};
  const normalizedTopic = (typeof topic === "string" ? topic : "").trim();
  const normalizedContent = (typeof content === "string" ? content : "").trim();

  if (!normalizedTopic) {
    errors.topic = "Escribe un tema.";
  } else if (normalizedTopic.length > MEMORY_TOPIC_MAX) {
    errors.topic = `El tema admite hasta ${MEMORY_TOPIC_MAX} caracteres.`;
  }

  if (!normalizedContent) {
    errors.content = "Escribe el dato.";
  } else if (normalizedContent.length > MEMORY_CONTENT_MAX) {
    errors.content = `El dato admite hasta ${MEMORY_CONTENT_MAX} caracteres.`;
  }

  if (normalizeAudiences(audiences).length === 0) {
    errors.audiences = "Elige al menos una audiencia.";
  }

  return errors;
}

/**
 * Body que viaja al backend: texto recortado, audiencias válidas y "all"
 * exclusivo (con "all" las demás no aportan nada: mandarlas solo ensucia el
 * dato guardado).
 * @param {{topic?: string, content?: string, audiences?: string[]}} draft
 * @returns {{topic: string, content: string, audiences: string[]}}
 */
export function draftToBody(draft = {}) {
  const audiences = normalizeAudiences(draft.audiences);
  return {
    topic: text(draft.topic).trim(),
    content: text(draft.content).trim(),
    audiences: audiences.includes("all") ? ["all"] : audiences,
  };
}

/**
 * Dato al que este fact reemplaza, tal como lo embebe el GET (`supersedes`).
 * El backend lo resuelve dentro del tenant y lo manda siempre que siga
 * existiendo; con `supersedes_id` pero sin objeto, el anterior fue borrado.
 * @param {object} fact
 * @returns {{id: string|null, topic: string, content: string}|null}
 */
export function resolveSuperseded(fact) {
  return fact?.supersedes || null;
}

/**
 * Campos editados respecto del dato original (body del PATCH de edición).
 * `{}` = nada cambió. Las audiencias se comparan como conjunto.
 * @param {object|undefined} original fact normalizado
 * @param {{topic: string, content: string, audiences: string[]}} body draftToBody(...)
 * @returns {{topic?: string, content?: string, audiences?: string[]}}
 */
export function editChanges(original, body) {
  if (!original) return { ...body };
  const changes = {};
  if (body.topic !== original.topic) changes.topic = body.topic;
  if (body.content !== original.content) changes.content = body.content;
  const before = [...(original.audiences || [])].sort().join(",");
  const after = [...body.audiences].sort().join(",");
  if (before !== after) changes.audiences = body.audiences;
  return changes;
}
